import { useState } from 'react'
import { formatTime, parseTimeToDate } from '../../helpers'
import {
  BusinessAvailableHours,
  DaysOfTheWeek,
} from '../../redux-toolkits/vendors/vendors.types'
import Dropdown from '../inputs/dropdown'
import { EditInput } from '../inputs/editInput'
import { TextInput } from '../inputs/textInput'
import CustSwitch from '../switch'

const times = [
  { name: '12 AM', value: '00:00' },
  { name: '1 AM', value: '01:00' },
  { name: '2 AM', value: '02:00' },
  { name: '3 AM', value: '03:00' },
  { name: '4 AM', value: '04:00' },
  { name: '5 AM', value: '05:00' },
  { name: '6 AM', value: '06:00' },
  { name: '7 AM', value: '07:00' },
  { name: '8 AM', value: '08:00' },
  { name: '9 AM', value: '09:00' },
  { name: '10 AM', value: '10:00' },
  { name: '11 AM', value: '11:00' },
  { name: '12 PM', value: '12:00' },
  { name: '1 PM', value: '13:00' },
  { name: '2 PM', value: '14:00' },
  { name: '3 PM', value: '15:00' },
  { name: '4 PM', value: '16:00' },
  { name: '5 PM', value: '17:00' },
  { name: '6 PM', value: '18:00' },
  { name: '7 PM', value: '19:00' },
  { name: '8 PM', value: '20:00' },
  { name: '9 PM', value: '21:00' },
  { name: '10 PM', value: '22:00' },
  { name: '11 PM', value: '23:00' },
]

const stripIdField = (data?: BusinessAvailableHours) => {
  return data
    ? (Object.entries(data).reduce(
        (acc: Partial<BusinessAvailableHours>, [day, details]) => {
          const { _id, ...rest } = details
          acc[day as DaysOfTheWeek] = rest
          return acc
        },
        {} as Partial<BusinessAvailableHours>,
      ) as BusinessAvailableHours)
    : undefined
}

export const AvailableHoursView = ({
  availableHours,
  className,
  loading = false,
  onModify = () => {},
}: {
  availableHours?: BusinessAvailableHours
  className?: string
  loading?: boolean
  onModify?: (data: BusinessAvailableHours) => void
}) => {
  const [isEdit, setIsEdit] = useState(false)

  const [payload, setPayload] = useState<BusinessAvailableHours | undefined>(
    stripIdField(availableHours),
  )

  const resetPayload = () => {
    setPayload(stripIdField(availableHours))
  }

  return payload ? (
    <EditInput
      title="Business Opening Hours"
      editing={isEdit}
      onEditSave={() => {
        onModify(payload)
        setIsEdit(false)
      }}
      onEditClick={() => {
        setIsEdit(true)
      }}
      onCancel={() => {
        setIsEdit(false)
        resetPayload()
      }}
      loading={loading}
      className={className}
    >
      {({ isEditing }) => {
        return (
          <div className="flex flex-col gap-4 w-fit mt-4">
            {Object.keys(payload).map((day, idx) => {
              const details = payload[day as DaysOfTheWeek]
              return (
                <div
                  key={`${idx}`}
                  className="flex flex-row items-center gap-4 w-fit"
                >
                  <p className={`w-fit min-w-[88px] capitalize`}>{day}</p>

                  {!isEditing && <p>•</p>}

                  {isEditing ? (
                    <div
                      className="flex items-center justify-center gap-4 w-fit"
                      key={`${idx}-controls`}
                    >
                      <CustSwitch
                        checked={details.open}
                        onChange={() => {
                          setPayload({
                            ...payload,
                            [day]: {
                              ...details,
                              open: !details.open,
                            },
                          })
                        }}
                      />

                      <span>{details.open ? 'Open' : 'Closed'}</span>
                    </div>
                  ) : (
                    <p className={`w-fit min-w-[53px]`}>
                      {details.open ? 'Open' : 'Closed'}
                    </p>
                  )}

                  {!isEditing && <p>•</p>}

                  {details.open && !isEditing ? (
                    <p>{`${formatTime(
                      parseTimeToDate(details.openingTime ?? '', 'HH:mm'),
                    )} to ${formatTime(
                      parseTimeToDate(details.closingTime ?? '', 'HH:mm'),
                    )}`}</p>
                  ) : undefined}

                  {isEditing && (
                    <div className="flex items-center gap-2">
                      <Dropdown
                        menuClassName="!max-h-[300px]"
                        menuButton={
                          <TextInput
                            hasIcon={false}
                            value={formatTime(
                              parseTimeToDate(
                                details.openingTime ?? '',
                                'HH:mm',
                              ),
                            )}
                            readOnly
                            placeholder="Opening Time"
                          />
                        }
                        menuItems={times.map((t) => ({
                          ...t,
                          onClick: () => {
                            setPayload({
                              ...payload,
                              [day]: {
                                ...details,
                                openingTime: t.value,
                                closingTime: '',
                              },
                            })
                          },
                        }))}
                      />

                      <p>to</p>

                      <Dropdown
                        menuClassName="!max-h-[300px]"
                        menuButton={
                          <TextInput
                            hasIcon={false}
                            value={formatTime(
                              parseTimeToDate(
                                details.closingTime ?? '',
                                'HH:mm',
                              ),
                            )}
                            readOnly
                            placeholder="Closing Time"
                          />
                        }
                        menuItems={times
                          .filter((t) => t.value > (details.openingTime ?? ''))
                          .map((t) => ({
                            ...t,
                            onClick: () => {
                              setPayload({
                                ...payload,
                                [day]: {
                                  ...details,
                                  closingTime: t.value,
                                },
                              })
                            },
                          }))}
                      />
                    </div>
                  )}
                </div>
              )
            })}
            <p></p>
          </div>
        )
      }}
    </EditInput>
  ) : undefined
}
